<template>
  <Card>
    <CardHeader v-if="title" :currentTheme="currentTheme" :collapsed="collapsed">
      <CardTitle @toggle-collapsed="toggleCollapsed">
        <span class="d-inline text-truncate">{{ $i18n.t(title) }}</span>
        <div v-if="additionalTitle" v-html="additionalTitle"></div>
        <span v-if="collapsible" class="material-icons ms-auto">{{ collapsed ? 'unfold_more' : 'unfold_less' }}</span>
      </CardTitle>

      <span v-if="this.helpy.content" class="pull-right mr5">
        <modal-opener class="btn bg-purple btn-xs" :id="helpy.title"
          ><span class="glyphicon glyphicon-info-sign"></span
        ></modal-opener>
        <modal :title="helpy.title" :id="helpy.title" class="helpy-modal"><div v-html="getContent()"></div></modal>
      </span>
    </CardHeader>
    <CardBody :class="[{ empty: empty, 'fix-height': fixHeight }]" :data-uid="panelUid" :style="height()">
      <div v-show="!collapsed">
        <slot v-if="renderSlot.main"></slot>
      </div>
      <div v-show="hasCollapsedSlot && collapsed">
        <slot v-if="renderSlot.collapsed" name="collapsed"></slot>
      </div>
    </CardBody>
    <div v-if="footer" class="panel-footer" :class="{ empty: empty }">
      <template-view :template="footer"></template-view>
    </div>
  </Card>
</template>

<script>
/**
 * @param Panel
 *
 * @description Az oldalon megjelenő comonensek (panelek) megjelenítését végzi
 */
import Modal from './modal/Modal'
import ModalOpener from './modal/Opener'
import Card from './card/Card'
import CardHeader from './card/CardHeader'
import CardTitle from './card/CardTitle'
import CardBody from './card/CardBody'
import Collapse from './Collapse'
import TemplateView from './list-view/TemplateView'
import { intersection, get } from 'lodash'
import EventBus from '../utils/event-bus'
import { guiThemes } from './layout-manager/lib/helpers'

export default {
  name: 'Panel',
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardBody,
    Collapse,
    Modal,
    ModalOpener,
    TemplateView,
  },
  props: {
    icon: String,
    title: String,
    helpy: {
      type: Object,
      default: () => ({
        title: null,
        content: null,
      }),
    },
    position: {
      type: Object,
      default: () => ({
        h: 10,
      }),
    },
    collapse: {
      type: Boolean,
      default: false,
    },
    collapsible: {
      type: Boolean,
      default: true,
    },
    fixHeight: {
      type: Boolean,
      default: false,
    },
    collapseStoreId: String,
    storeCollapseState: {
      type: Boolean,
      default: true,
    },
    footer: String,
    panelUid: String,
    additionalTitle: String,
  },
  data: () => ({
    collapsed: null,
    renderSlot: {
      main: false,
      collapsed: false,
    },
  }),
  computed: {
    empty() {
      return !this.hasCollapsedSlot && this.collapsed
    },
    collapseStorable() {
      return this.storeCollapseState && this.collapseStoreId
    },
    hasCollapsedSlot() {
      return !!this.$slots.collapsed
    },
    availableThemes: () => guiThemes().map((theme) => theme.value),
    currentTheme() {
      const body = document.getElementsByTagName('body')[0]
      return intersection(this.availableThemes, body.classList)[0]
    },
  },
  watch: {
    collapsed(newValue) {
      this.$emit('collapse-changed', newValue)
      if (!newValue) {
        this.renderSlot.main = true
      } else if (this.hasCollapsedSlot) {
        this.renderSlot.collapsed = true
      }
    },
    collapse(newValue) {
      this.collapsed = newValue
      if (this.collapseStorable) {
        localStorage[this.collapseStoreId] = this.collapsed
        EventBus.$emit('panel-collapse')
      }
    },
  },
  methods: {
    height() {
      if (!this.empty) {
        const height = parseInt(this.position.h) * 40
        if (this.fixHeight) return `height:${height}px`
      }
      return null
    },
    toggleCollapsed() {
      this.collapsed = !this.collapsed
      if (this.collapseStorable) {
        // @todo panelStore letrehozasa
        localStorage[this.collapseStoreId] = this.collapsed
        EventBus.$emit('panel-collapse')
      }
    },
    getContent() {
      return this.helpy.content.replace(/(?:\r\n|\r|\n)/g, '<br/>')
    },
  },
  created() {
    this.collapsed = this.collapse
    if (this.collapseStorable && localStorage[this.collapseStoreId]) {
      this.collapsed = JSON.parse(localStorage[this.collapseStoreId])
    }
  },
}
</script>

<style lang="scss">
.panel-body.fix-height {
  overflow-y: auto;
}
.helpy-modal .modal-body {
  line-height: 20px;
}
.panel-body {
  background-color: white;
  padding: 20px !important;
  &.empty {
    padding: 0 !important;
  }
  &:last-child {
    -webkit-border-bottom-right-radius: 0.3rem !important;
    -webkit-border-bottom-left-radius: 0.3rem !important;
    border-bottom-right-radius: 0.3rem !important;
    border-bottom-left-radius: 0.3rem !important;
  }
}
.panel-heading {
  -webkit-border-top-right-radius: 0.3rem !important;
  -webkit-border-top-left-radius: 0.3rem !important;
  border-top-right-radius: 0.3rem !important;
  border-top-left-radius: 0.3rem !important;
  border-bottom: 1px solid $border-color;
}
</style>

<style scoped lang="scss">
.panel .material-icons {
  cursor: pointer;
}
.panel {
  border: 1px solid $border-color;
  border-radius: 5px;
  box-shadow: rgba(0, 0, 0, 0.05) 0 0 0 1px;
  z-index: 0;
}
.panel-heading,
.panel-footer {
  background-color: $default-panel-header-bg;
}
.panel-footer {
  border-radius: 0 0 0.3rem 0.3rem;
  border-top: 1px solid $border-color;
  padding: 20px;
}
.panel-heading {
  font-size: 0.929em;
  min-height: 2.857em;
  line-height: 2.857em;
  border-radius: 10px 10px 0 0;
}
.panel-title {
  padding: 5px 15px 5px 15px;
  display: flex;
  font-size: 16px;
  align-items: center;
}
.default {
  color: $default-panel-icon-color !important;
  + .panel-title {
    color: $default-panel-title-color !important;
  }
}
.blue {
  color: $blue-panel-icon-color !important;
  + .panel-title {
    color: $blue-panel-title-color !important;
  }
}
.green {
  color: $green-panel-icon-color !important;
  + .panel-title {
    color: $green-panel-title-color !important;
  }
}
.orange {
  color: $orange-panel-icon-color !important;
  + .panel-title {
    color: $orange-panel-title-color !important;
  }
}
.pink {
  color: $pink-panel-icon-color !important;
  + .panel-title {
    color: $pink-panel-title-color !important;
  }
}
.red {
  color: $red-panel-icon-color !important;
  + .panel-title {
    color: $red-panel-title-color !important;
  }
}
span {
  + .panel-title {
    font-size: 14px;
    margin-right: auto;
  }
}
.d-flex {
  align-items: baseline;
}
.d-inline {
  display: inline;
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: bold;
  color: #504a64;
}
.glyphicon {
  padding: 0 0 0.5rem 0 !important;
}
.footer.empty {
  padding: 0;
  border-top: none;
}
.empty .hide-if-empty {
  display: none !important;
}
</style>
